import { useState } from 'react';
import type { GuardianRequest } from '@teentack/shared';
import { useGuardianDecision, useGuardianRequests } from '@/hooks/use-guardian-requests';
import { ParentShell } from '@/features/parent/ParentShell';
import { EmptyState } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorNote } from '@/components/Form';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { BadgeCheckIcon, UsersIcon } from '@/components/ui/Icons';

const WHEN = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function formatWhen(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : WHEN.format(date);
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return `${parts[0]?.charAt(0) ?? ''}${parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : ''}`.toUpperCase();
}

/**
 * The guardian's decision surface. A minor stays PENDING and cannot apply for
 * any work until their guardian approves here. [FR-M01.2]
 */
export function GuardianRequestsPage() {
  const { data, isPending, isError, error, refetch } = useGuardianRequests();
  const decision = useGuardianDecision();

  /** Which row is mid-request, so only that row shows a busy state. */
  const [working, setWorking] = useState<string | null>(null);
  /** Reject is destructive for the teen, so it asks first. */
  const [confirming, setConfirming] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  async function decide(request: GuardianRequest, choice: 'approve' | 'reject'): Promise<void> {
    setFailure(null);
    setWorking(request.relationshipId);
    try {
      await decision.mutateAsync({ relationshipId: request.relationshipId, decision: choice });
      setConfirming(null);
    } catch (caught) {
      setFailure(
        caught instanceof Error ? caught.message : 'Could not record that. Please try again.',
      );
    } finally {
      setWorking(null);
    }
  }

  return (
    <ParentShell
      title="Teen accounts"
      description="A teen under 18 cannot use TeenTack until you approve their account. Only approve a name you recognise — approving activates them straight away."
    >
      <ErrorNote message={failure} />

      {isPending && (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      )}

      {isError && (
        <div className="rounded-2xl border border-line bg-white p-8 text-center shadow-soft">
          <p className="text-sm text-slate-500">
            {error instanceof Error ? error.message : 'Could not load your requests.'}
          </p>
          <Button className="mt-5" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      )}

      {data && data.length === 0 && (
        <EmptyState icon={<BadgeCheckIcon className="size-6" />} title="Nothing waiting on you">
          When a teen names this email address as their parent or guardian, their request appears
          here. We will email you as soon as one arrives.
        </EmptyState>
      )}

      {data && data.length > 0 && (
        <>
          <p className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-500">
            <UsersIcon className="size-4" />
            {data.length} teen{data.length === 1 ? '' : 's'} waiting for your decision
          </p>

          <ul className="space-y-4">
            {data.map((request) => {
              const busy = working === request.relationshipId;
              const isConfirming = confirming === request.relationshipId;

              return (
                <li
                  key={request.relationshipId}
                  className="rounded-2xl border border-line bg-white p-5 shadow-soft sm:p-6"
                >
                  <div className="flex flex-wrap items-center gap-4">
                    <span
                      aria-hidden="true"
                      className="grid size-12 shrink-0 place-items-center rounded-xl bg-slate-100 text-sm font-extrabold text-slate-500"
                    >
                      {initialsOf(request.teenName)}
                    </span>

                    <div className="min-w-0 flex-1">
                      <p className="text-base font-bold text-ink">{request.teenName}</p>
                      <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <Badge tone="neutral">{request.relationshipType}</Badge>
                        <span>Requested {formatWhen(request.requestedAt)}</span>
                      </p>
                    </div>

                    {!isConfirming && (
                      <div className="flex w-full gap-2 sm:w-auto">
                        <Button
                          variant="success"
                          onClick={() => void decide(request, 'approve')}
                          disabled={busy}
                          aria-busy={busy || undefined}
                          className="flex-1 sm:flex-none"
                        >
                          {busy ? 'Working…' : 'Approve'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setConfirming(request.relationshipId)}
                          disabled={busy}
                          className="flex-1 sm:flex-none"
                        >
                          Decline
                        </Button>
                      </div>
                    )}
                  </div>

                  {isConfirming && (
                    <div className="mt-4 rounded-xl border border-sun-200 bg-sun-50 p-4">
                      <p className="text-sm font-medium text-sun-700">
                        Decline {request.teenName}? Their account stays blocked and they will be
                        told. They can ask again later.
                      </p>
                      <div className="mt-3 flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => void decide(request, 'reject')}
                          disabled={busy}
                          aria-busy={busy || undefined}
                        >
                          {busy ? 'Working…' : 'Yes, decline'}
                        </Button>
                        <Button variant="ghost" onClick={() => setConfirming(null)} disabled={busy}>
                          Keep it pending
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </ParentShell>
  );
}
